import { Box, Chip, Paper, Typography } from "@mui/material";
import dayjs from "dayjs";
import { ClientBillingSubscription } from "../../types/billing";
import { formatDateRange, subscriptionStatusColor } from "../../utils/billingFormat";
import { formatCurrency } from "../../utils/formatCurrency";
import { BillingField } from "./BillingCustomerPane";
import ProjectedInvoiceTable from "./ProjectedInvoiceTable";

interface Props {
  /** The Stripe id is admin-only; the client payload never carries it and the id line is omitted. */
  subscription: ClientBillingSubscription & { id?: string };
}

export default function BillingSubscriptionCard({ subscription }: Props) {
  const name = subscription.name;
  const upcomingInvoice = subscription.upcoming_invoice;
  const formatMoney = (cents: number) => formatCurrency(cents, subscription.currency);

  return (
    <Paper variant="outlined" component="section" aria-label={name} sx={{ p: 2.5 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
        <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>{name}</Typography>
        <Chip label={subscription.status} size="small" color={subscriptionStatusColor(subscription.status)} variant="outlined" />
      </Box>
      {subscription.id && (
        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "monospace", mt: 0.5 }}>{subscription.id}</Typography>
      )}

      <Box component="section" sx={{ mt: 2.5 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, gap: 3 }}>
          <BillingField label="Current period">
            {formatDateRange(subscription.current_period_start, subscription.current_period_end)}
          </BillingField>
          <BillingField label="Projected total">{upcomingInvoice ? formatMoney(upcomingInvoice.total) : "-"}</BillingField>
          {upcomingInvoice?.next_payment_attempt && (
            <BillingField label="Next charge">{dayjs(upcomingInvoice.next_payment_attempt).format("MMM D, YYYY")}</BillingField>
          )}
          {subscription.billing_threshold_amount != null && (
            <BillingField label="Auto-invoice threshold">{formatMoney(subscription.billing_threshold_amount)}</BillingField>
          )}
        </Box>

        {upcomingInvoice ? (
          <Box sx={{ overflowX: "auto", mt: 4 }}>
            <ProjectedInvoiceTable upcomingInvoice={upcomingInvoice} items={subscription.items} />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>No upcoming invoice.</Typography>
        )}
      </Box>
    </Paper>
  );
}
